<section>
  <onyx-information-heading>
    {{ `${I18N}.generalInformation` | translate }}
  </onyx-information-heading>

  <onyx-information-list>
    @if (vehicle().generalInformation; as generalInformation) {
      <onyx-information-row label="labels.vehicle">
        <span>
          {{
            `${isVehicle() ? DictionaryCode.VEHICLE_CATEGORY : DictionaryCode.TRAILER_CATEGORY}.${generalInformation.category}`
              | translate
          }}

          @if (generalInformation.type; as type) {
            <span>·</span>

            {{ `${DictionaryCode.VEHICLE_TYPE}.${type}` | translate }}
          }
        </span>
      </onyx-information-row>

      <onyx-information-row label="labels.registrationNumber">
        {{ generalInformation.registrationNumber }}
      </onyx-information-row>

      <onyx-information-row label="labels.sideNumber">
        {{ generalInformation.sideNumber ?? '-' }}
      </onyx-information-row>

      <onyx-information-row label="labels.vinNumber">
        {{ generalInformation.vinNumber }}
      </onyx-information-row>

      <onyx-information-row label="labels.makeAndModel">
        {{ generalInformation.make }} {{ generalInformation.model }}
      </onyx-information-row>

      <onyx-information-row label="labels.manufactureYear">
        {{ generalInformation.manufactureYear }}
      </onyx-information-row>

      <onyx-information-row label="labels.firstRegistrationDate">
        {{ generalInformation.firstRegistrationDate | onyxDate: 'date-dot' }}
      </onyx-information-row>

      <onyx-information-row label="labels.registrationCountry">
        <app-country-cell
          [countryCode]="generalInformation.registrationCountry"
        ></app-country-cell>
      </onyx-information-row>

      <onyx-information-row label="labels.state">
        {{
          `${DictionaryCode.FLEET_STATE}.${generalInformation.state}`
            | translate
        }}
      </onyx-information-row>

      @if (generalInformation.planningMode; as planningMode) {
        <onyx-information-row label="labels.planning">
          {{
            `${DictionaryCode.VEHICLE_PLANNING_MODE}.${planningMode}`
              | translate
          }}
        </onyx-information-row>
      }

      @let totalDistance =
        vehicle().telemetry?.totalDistance ??
        vehicle().generalInformation.initialMileage;
      @if (totalDistance != null) {
        <onyx-information-row label="labels.mileage">
          {{ totalDistance | onyxDistance }}
        </onyx-information-row>
      }

      @let serviceDistance = vehicle().telemetry?.serviceDistance;
      @if (serviceDistance != null) {
        @let isServiceRequired = serviceDistance === 0;
        <onyx-information-row
          label="labels.nextService"
          [color]="isServiceRequired ? 'red' : 'gray'"
        >
          {{
            isServiceRequired
              ? ('labels.serviceRequired' | translate)
              : ('fleet.fleetList.cells.nextService'
                | translate: { distance: serviceDistance | onyxDistance })
          }}

          @if (totalDistance != null) {
            ·

            {{ totalDistance + serviceDistance | onyxDistance }}
          }

          <div class="actions">
            <onyx-icon-button
              variant="transparent"
              color="gray"
              [size]="24"
              icon="service-filled"
              onyxTooltip="labels.scheduleService"
              (click)="scheduleService()"
            ></onyx-icon-button>
          </div>
        </onyx-information-row>
      }

      <onyx-information-row label="labels.base">
        {{ generalInformation.assignedBase?.name ?? '-' }}
      </onyx-information-row>
    }

    <onyx-information-row label="labels.telematics">
      @if (vehicle().telematics; as telematics) {
        {{
          `${DictionaryCode.TELEMATICS_PROVIDER}.${telematics.provider}`
            | translate
        }}

        <span class="f-muted">· {{ telematics.deviceId }}</span>
      } @else {
        -
      }
    </onyx-information-row>
  </onyx-information-list>
</section>

<section>
  @if (vehicle().generalInformation.category; as category) {
    <onyx-information-heading>
      {{
        `fleet.fleetForm.vehicleMeasurements.heading.${
          isVehicle() ? 'vehicle' : category
        }` | translate
      }}
    </onyx-information-heading>
  }

  <onyx-information-list>
    @let vehicleMeasurements = vehicle().vehicleMeasurements;
    @if (vehicleMeasurements.curbWeight; as curbWeight) {
      <onyx-information-row label="labels.curbWeight">
        <app-unit-cell [value]="curbWeight" unit="kg"></app-unit-cell>
      </onyx-information-row>
    }

    @if (vehicleMeasurements.axleCount; as axleCount) {
      <onyx-information-row label="labels.axleCount">
        {{ axleCount }}
      </onyx-information-row>
    }

    <onyx-information-row label="labels.vehicleDimensions">
      {{ vehicleMeasurements | dimensions }}
    </onyx-information-row>
  </onyx-information-list>
</section>

@if (vehicle().generalInformation.category !== FleetCategory.SEMI_TRUCK) {
  <section>
    <onyx-information-heading>
      {{ `${I18N}.cargoSpace` | translate }}
    </onyx-information-heading>

    <onyx-information-list>
      @let cargoSpace = vehicle().cargoSpace;
      <onyx-information-row label="labels.loadCapacity">
        <app-unit-cell
          [value]="cargoSpace.loadCapacity"
          unit="kg"
        ></app-unit-cell>
      </onyx-information-row>

      @if (cargoSpace.volumeCapacity; as volumeCapacity) {
        <onyx-information-row label="labels.volume">
          <app-unit-cell
            [value]="volumeCapacity"
            [decimalPlaces]="2"
            unit="m³"
          ></app-unit-cell>
        </onyx-information-row>
      }

      @if (cargoSpace.chambersNumber; as chambersNumber) {
        <onyx-information-row label="labels.chambersNumber">
          {{ chambersNumber }}
        </onyx-information-row>
      }

      @if (cargoSpace.tankCertificates?.length) {
        <onyx-information-row label="labels.tankCertificates">
          <app-string-cell
            [value]="cargoSpace.tankCertificates"
            [dictionaryCode]="DictionaryCode.TANK_CERTIFICATE"
          ></app-string-cell>
        </onyx-information-row>
      }

      @if (cargoSpace.dischargeTypes?.length) {
        <onyx-information-row label="labels.dischargeType">
          <app-string-cell
            [value]="cargoSpace.dischargeTypes"
            [dictionaryCode]="DictionaryCode.DISCHARGE_TYPE"
          ></app-string-cell>
        </onyx-information-row>
      }

      @if (cargoSpace.europalletsNumber; as europalletsNumber) {
        <onyx-information-row label="labels.europalletsNumber">
          <app-unit-cell
            [value]="europalletsNumber"
            [unit]="'units.pieces' | translate"
          ></app-unit-cell>
        </onyx-information-row>
      }

      @let dimensions = cargoSpace.dimensions;
      @if (
        dimensions.length != null &&
        dimensions.width != null &&
        dimensions.height != null
      ) {
        <onyx-information-row label="labels.cargoSpaceDimensions">
          {{ $any(dimensions) | dimensions }}
        </onyx-information-row>
      }
    </onyx-information-list>
  </section>
}

@if (isVehicle()) {
  <section>
    <onyx-information-heading>
      {{ 'fleet.fleetForm.fuelTankCapacity.heading' | translate }}
    </onyx-information-heading>

    <onyx-information-list>
      @let fuelTankCapacity = vehicle().fuelTankCapacity!;
      <onyx-information-row label="labels.fuel">
        {{
          `${DictionaryCode.FUEL_TYPE}.${fuelTankCapacity.fuelType}` | translate
        }}
      </onyx-information-row>

      <onyx-information-row label="labels.mainFuelTank">
        <app-unit-cell
          [value]="fuelTankCapacity.mainTank"
          [decimalPlaces]="2"
          [unit]="fuelType()?.units?.[0] ?? ''"
        ></app-unit-cell>
      </onyx-information-row>

      @if (fuelType()?.requireAdditionalTank) {
        <onyx-information-row label="labels.additionalTank">
          <app-unit-cell
            [value]="fuelTankCapacity.additionalTank"
            [decimalPlaces]="2"
            [unit]="fuelType()?.units?.[1] ?? ''"
          ></app-unit-cell>
        </onyx-information-row>
      }

      <onyx-information-row label="labels.adBlue">
        <app-unit-cell
          [value]="fuelTankCapacity.adBlueTank"
          [decimalPlaces]="2"
          unit="L"
        ></app-unit-cell>
      </onyx-information-row>

      <onyx-information-row label="labels.generatorFuelTank">
        <app-unit-cell
          [value]="fuelTankCapacity.generatorFuelTank"
          [decimalPlaces]="2"
          unit="L"
        ></app-unit-cell>
      </onyx-information-row>
    </onyx-information-list>
  </section>
}

<section>
  <onyx-information-heading>
    {{ 'labels.ownershipType' | translate }}
  </onyx-information-heading>

  <onyx-information-list>
    @let ownership = vehicle().ownership;
    <onyx-information-row label="labels.forSale">
      {{ (ownership.isForSale ? 'labels.yes' : 'labels.no') | translate }}
    </onyx-information-row>

    @let ownershipType = vehicle().ownership.type;
    <onyx-information-row label="labels.ownershipType">
      {{ `${DictionaryCode.OWNERSHIP_TYPE}.${ownershipType}` | translate }}
    </onyx-information-row>

    @switch (ownershipType) {
      @case (OwnershipType.OWN) {
        <onyx-information-row label="labels.vehicleValue">
          <app-amount-cell [amount]="ownership.vehicleValue"></app-amount-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.depreciationPeriod">
          <app-date-range-cell
            [range]="{
              from: ownership.depreciationPeriod!.from!,
              to: ownership.depreciationPeriod!.to!,
            }"
          ></app-date-range-cell>
        </onyx-information-row>
      }
      @case (OwnershipType.RENTAL) {
        <onyx-information-row label="labels.selfDeposit">
          <app-amount-cell [amount]="ownership.selfDeposit"></app-amount-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.installment">
          <app-amount-cell [amount]="ownership.installment"></app-amount-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.firstInstallment">
          {{
            ownership.firstAndLastInstallmentDate!.from! | onyxDate: 'date-dot'
          }}
        </onyx-information-row>

        <onyx-information-row label="labels.lastInstallment">
          {{
            ownership.firstAndLastInstallmentDate!.to! | onyxDate: 'date-dot'
          }}
        </onyx-information-row>
      }
      @default {
        <onyx-information-row label="labels.selfDeposit">
          <app-amount-cell [amount]="ownership.selfDeposit"></app-amount-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.buyout">
          <app-amount-cell [amount]="ownership.buyout"></app-amount-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.installment">
          <app-amount-cell [amount]="ownership.installment"></app-amount-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.firstInstallment">
          {{
            ownership.firstAndLastInstallmentDate!.from! | onyxDate: 'date-dot'
          }}
        </onyx-information-row>

        <onyx-information-row label="labels.lastInstallment">
          {{
            ownership.firstAndLastInstallmentDate!.to! | onyxDate: 'date-dot'
          }}
        </onyx-information-row>

        <onyx-information-row label="labels.generatorFuelTank">
          <app-unit-cell [value]="ownership.margin" unit="%"></app-unit-cell>
        </onyx-information-row>

        <onyx-information-row label="labels.interestRate">
          <app-string-cell
            [value]="ownership.interestRateType"
            [dictionaryCode]="DictionaryCode.INTEREST_RATE_TYPE"
          ></app-string-cell>
        </onyx-information-row>
      }
    }
  </onyx-information-list>
</section>

<section>
  <onyx-information-heading>
    {{ 'forms.countriesWhitelist.heading' | translate }}
  </onyx-information-heading>

  @if (vehicle().allowedCountries; as allowedCountries) {
    <onyx-information-row label="forms.countriesWhitelist.group">
      <app-allowed-countries
        [allowedCountries]="allowedCountries"
      ></app-allowed-countries>
    </onyx-information-row>
  }
</section>

@if (isVehicle() && vehicle().tachograph; as tachograph) {
  <section>
    <onyx-information-heading>
      {{ 'labels.tachograph' | translate }}
    </onyx-information-heading>

    <onyx-information-list>
      <onyx-information-row label="labels.model">
        <app-string-cell
          [value]="tachograph.model"
          [dictionaryCode]="DictionaryCode.TACHOGRAPH_MODEL"
        ></app-string-cell>
      </onyx-information-row>

      <onyx-information-row label="labels.make">
        <app-string-cell
          [value]="tachograph.make"
          [dictionaryCode]="DictionaryCode.TACHOGRAPH_MAKE"
        ></app-string-cell>
      </onyx-information-row>

      <app-modal-date-row
        label="labels.nextLegalizationDate"
        [expirationDate]="tachograph.nextLegalizationDate"
        [size]="184"
      >
        <div class="actions">
          <onyx-icon-button
            variant="transparent"
            color="gray"
            [size]="24"
            icon="service-filled"
            onyxTooltip="labels.scheduleService"
            (click)="scheduleService()"
          ></onyx-icon-button>
        </div>
      </app-modal-date-row>
    </onyx-information-list>
  </section>
}
